import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiDel, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Search, Star, BadgeCheck, ArrowUpRight, Trash2, Phone, MapPin, ChevronDown, ChevronRight, Plus, X, IndianRupee } from 'lucide-react';

const PROCUREMENT_ROLES = new Set(['md', 'super_admin', 'ceo', 'cmd', 'cfo', 'procurement_head']);

const fmtRating = (r) => {
  if (r === null || r === undefined || r === '') return '—';
  return `${Number(r).toFixed(1)} ★`;
};

export default function PotentialVendors() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});  // {pv_id: bool}
  const [rateDraft, setRateDraft] = useState({}); // {pv_id: [{item,uom,rate}]}
  const canPromote =
    PROCUREMENT_ROLES.has((user?.role || '').toLowerCase()) ||
    (user?.allowed_modules || []).includes('procurement') ||
    (user?.allowed_modules || []).includes('*');

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/potential-vendors', q ? { q } : undefined);
      setItems(r.items || []);
    } catch {
      toast.error('Could not load potential vendors');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const promote = async (it) => {
    if (!window.confirm(`Promote "${it.name}" into the Vendor Master?\n\nThis creates an empanelment-pending record in db.vendors.`)) return;
    setBusyId(it.id);
    try {
      const r = await apiPost(`/potential-vendors/${it.id}/promote`, {});
      toast.success(`Promoted — vendor code ${r.vendor_code}`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Promote failed';
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Soft-delete "${it.name}"?`)) return;
    try {
      await apiDel(`/potential-vendors/${it.id}`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  // ── Quoted-rates inline editor ──
  const toggleExpand = (it) => {
    const nowOpen = !expanded[it.id];
    setExpanded((p) => ({ ...p, [it.id]: nowOpen }));
    if (nowOpen) {
      setRateDraft((p) => ({ ...p, [it.id]: (it.quoted_rates || []).map((r) => ({ ...r })) }));
    }
  };
  const updRate = (pid, idx, patch) =>
    setRateDraft((p) => ({ ...p, [pid]: p[pid].map((r, i) => (i === idx ? { ...r, ...patch } : r)) }));
  const addRate = (pid) =>
    setRateDraft((p) => ({ ...p, [pid]: [...(p[pid] || []), { item: '', uom: '', rate: '' }] }));
  const delRate = (pid, idx) =>
    setRateDraft((p) => ({ ...p, [pid]: p[pid].filter((_, i) => i !== idx) }));
  const saveRates = async (pid) => {
    // Tendering_30Sep2026 §7: keep a rate even when its item is blank —
    // previously any row without an item was silently dropped here.
    const payload = (rateDraft[pid] || [])
      .filter((r) => Number(r.rate) > 0)
      .map((r) => ({ item: (r.item || '').trim(), uom: (r.uom || '').trim(), rate: Number(r.rate) }));
    const droppedForNoRate = (rateDraft[pid] || []).length - payload.length;
    setBusyId(pid);
    try {
      await apiPut(`/potential-vendors/${pid}/quoted-rates`, payload);
      toast.success(droppedForNoRate > 0
        ? `${payload.length} rate(s) saved · ${droppedForNoRate} skipped (no rate entered)`
        : `${payload.length} rate(s) saved`);
      load();
      setExpanded((p) => ({ ...p, [pid]: false }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="potential-vendors-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-display">Potential Vendors</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Vendors captured during Site Visits — for rate enquiry only. Promote to
            Vendor Master only once the project is won.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search name…"
              className="border border-slate-300 rounded pl-7 pr-2 py-1.5 text-sm w-56"
              data-testid="potential-vendors-search"
            />
          </div>
          <button
            onClick={load}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            Search
          </button>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden shadow-sm">
        <table className="w-full" data-testid="potential-vendors-table">
          <thead className="bg-slate-50">
            <tr className="text-xs uppercase tracking-wider text-slate-500">
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2">Location</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-left px-3 py-2">Rating</th>
              <th className="text-left px-3 py-2">Source Enquiry</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-6 text-center text-sm text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-8 text-center text-sm text-slate-500">
                  No potential vendors yet. They are captured from the Site Visit Report's
                  "Nearby Suppliers" picker.
                </td>
              </tr>
            ) : items.map((it) => (
              <React.Fragment key={it.id}>
              <tr className="border-t hover:bg-slate-50" data-testid={`pv-row-${it.id}`}>
                <td className="px-3 py-2">
                  <button
                    onClick={() => toggleExpand(it)}
                    className="inline-flex items-center gap-1 text-left"
                    data-testid={`pv-expand-${it.id}`}
                  >
                    {expanded[it.id] ? <ChevronDown size={12} className="text-slate-400" />
                                     : <ChevronRight size={12} className="text-slate-400" />}
                    <div>
                      <div className="text-sm font-medium text-slate-800">{it.name}</div>
                      {it.contact_person && (
                        <div className="text-[11px] text-slate-500">{it.contact_person}</div>
                      )}
                    </div>
                  </button>
                  {(it.quoted_rates?.length > 0) && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                          title={`${it.quoted_rates.length} quoted rate(s) on file`}>
                      <IndianRupee size={9} /> {it.quoted_rates.length} rates
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-sm text-slate-700">{it.category || '—'}</td>
                <td className="px-3 py-2 text-sm text-slate-700">
                  {it.location ? (
                    <span className="inline-flex items-center gap-1"><MapPin size={11} />{it.location}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 text-sm text-slate-700">
                  {it.phone ? (
                    <span className="inline-flex items-center gap-1"><Phone size={11} />{it.phone}</span>
                  ) : '—'}
                </td>
                <td className="px-3 py-2 text-sm tabular-nums">
                  {it.rating ? <span className="text-amber-600 font-semibold">{fmtRating(it.rating)}</span> : '—'}
                </td>
                <td className="px-3 py-2 text-xs">
                  {it.source_enquiry_id ? (
                    <Link to={`/app/estimation/enquiries/${it.source_enquiry_id}`} className="text-indigo-700 hover:text-indigo-900">
                      {it.source_enquiry_no || it.source_enquiry_id.slice(0, 8)}
                    </Link>
                  ) : '—'}
                </td>
                <td className="px-3 py-2">
                  {it.promoted_vendor_id ? (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`pv-promoted-${it.id}`}>
                      <BadgeCheck size={11} /> Promoted
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                      <Star size={11} /> Potential
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {!it.promoted_vendor_id && canPromote && (
                    <button
                      onClick={() => promote(it)}
                      disabled={busyId === it.id}
                      data-testid={`pv-promote-${it.id}`}
                      className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:text-indigo-900 font-medium mr-3 disabled:opacity-50"
                    >
                      <ArrowUpRight size={12} /> Promote to Vendor Master
                    </button>
                  )}
                  {it.promoted_vendor_id && (
                    <Link
                      to={`/app/procurement/vendors/${it.promoted_vendor_id}`}
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-900 mr-3"
                    >
                      <ArrowUpRight size={12} /> Open vendor
                    </Link>
                  )}
                  <button onClick={() => remove(it)} className="text-rose-600 hover:text-rose-800" title="Soft-delete">
                    <Trash2 size={12} />
                  </button>
                </td>
              </tr>
              {expanded[it.id] && (
                <tr data-testid={`pv-rates-row-${it.id}`}>
                  <td colSpan={8} className="bg-slate-50 px-6 py-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-600">
                        <IndianRupee size={11} className="inline mb-0.5" /> Quoted rates from this vendor
                      </h4>
                      <button onClick={() => addRate(it.id)} className="text-xs text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1"
                              data-testid={`pv-add-rate-${it.id}`}>
                        <Plus size={11} /> Add row
                      </button>
                    </div>
                    {(rateDraft[it.id] || []).length === 0 ? (
                      <p className="text-xs text-slate-500 italic py-2">No rates yet — click <b>Add row</b>.</p>
                    ) : (
                      <table className="w-full text-xs mb-2">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                            <th className="text-left py-1">Item / Material</th>
                            <th className="text-left py-1 w-24">UOM</th>
                            <th className="text-right py-1 w-32">Rate (₹)</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(rateDraft[it.id] || []).map((r, i) => (
                            <tr key={i}>
                              <td className="py-0.5 pr-2">
                                <input value={r.item} onChange={(e) => updRate(it.id, i, { item: e.target.value })}
                                       placeholder="e.g. Cement OPC 53 grade"
                                       className="w-full border border-slate-300 rounded px-2 py-1 text-xs"
                                       data-testid={`pv-rate-item-${it.id}-${i}`} />
                              </td>
                              <td className="py-0.5 pr-2">
                                <input value={r.uom || ''} onChange={(e) => updRate(it.id, i, { uom: e.target.value })}
                                       placeholder="bag/cum/MT"
                                       className="w-full border border-slate-300 rounded px-2 py-1 text-xs" />
                              </td>
                              <td className="py-0.5 pr-2">
                                <input type="number" value={r.rate} onChange={(e) => updRate(it.id, i, { rate: e.target.value })}
                                       className="w-full border border-slate-300 rounded px-2 py-1 text-xs text-right tabular-nums"
                                       data-testid={`pv-rate-value-${it.id}-${i}`} />
                              </td>
                              <td>
                                <button onClick={() => delRate(it.id, i)} className="text-rose-500 hover:text-rose-700">
                                  <X size={11} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="flex justify-end gap-2 mt-2 pt-2 border-t">
                      <button onClick={() => setExpanded((p) => ({ ...p, [it.id]: false }))}
                              className="px-3 py-1 text-xs text-slate-600 hover:text-slate-900">
                        Cancel
                      </button>
                      <button onClick={() => saveRates(it.id)} disabled={busyId === it.id}
                              data-testid={`pv-save-rates-${it.id}`}
                              className="px-3 py-1 text-xs bg-indigo-700 text-white rounded hover:bg-indigo-800 disabled:opacity-50">
                        Save rates
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
